import i18next from 'i18next';

export interface StopInfo {
  stopName: string;
  utilization: number;
  delivery: number;
  pickup: number;
  remainingCapacity: number;
}
export type weightWatcherData = StopInfo[];

interface Shipment {
  custom_id: string;
  adr_points?: number;
  loads?: number[];
  customer_name?: string;
}

interface Address {
  city?: string;
  postal_code?: string;
}

interface Stop {
  type: 'garage' | 'depot' | 'customer';
  location?: { address?: Address };
  shipments_to_unload?: string[];
  shipments_to_load?: string[];
}

interface Route {
  shipments?: Shipment[];
  stops?: Stop[];
  present_shipments_at_start?: string[];
}

interface Vehicle {
  max_adr_points?: number;
  capacities?: number[];
}

export const extractStopsLoadV2 = (
  route: any,
  dimensionIndex: number,
  isADRPoints: boolean,
  vehicle: any,
): weightWatcherData => {
  const shipmentsById: Record<string, Shipment> = {};
  for (const shp of route.shipments || []) shipmentsById[shp.custom_id] = shp;

  // Capacity for chosen dimension
  const capacity = isADRPoints
    ? (vehicle.max_adr_points ?? 0)
    : ((vehicle?.capacities && vehicle.capacities[dimensionIndex]) ?? 0);
  // Load present at route start
  let arrivalLoad = 0;
  for (const cid of route?.present_shipments_at_start || []) {
    const shp = shipmentsById[cid];
    if (!shp) continue;
    arrivalLoad += isADRPoints
      ? (shp.loads?.[2] ?? 0)
      : (shp.loads?.[dimensionIndex] ?? 0);
  }

  const result: weightWatcherData = [];

  for (const stop of route.stops || []) {
    if (stop.type === 'garage') continue;
    // Label
    let stopName = '';
    const address = stop.location?.address || {};
    const city = address.city || '';
    const postal = address.postal_code || '';

    const relatedShipmentIds = [...(stop.shipments_to_unload || []), ...(stop.shipments_to_load || [])];
    const relatedShipment = relatedShipmentIds.map(id => shipmentsById[id]).find(Boolean);
    const customerName = relatedShipment?.customer_name?.trim() || '';

    if (stop.type === 'depot' || stop.type === 'garage') {
      const prefix = stop.type === 'garage' ? 'GARAGE' : 'DEPOT';
      stopName = (postal || city)
        ? `${prefix}: ${[postal, city].filter(Boolean).join(' ')}`
        : prefix;
    } else {
      stopName = customerName || [postal, city].filter(Boolean).join(' ') || (i18next.t('common:weightWatcher.unknownStop') || 'UNKNOWN');
    }

    let unloadSum = 0;
    for (const cid of stop.shipments_to_unload || []) {
      const shp = shipmentsById[cid];
      if (!shp) continue;
      unloadSum += isADRPoints
        ? (shp.loads?.[2] ?? 0) // ADR always in index 2
        : (shp.loads?.[dimensionIndex] ?? 0);
    }

    let loadSum = 0;
    for (const cid of stop.shipments_to_load || []) {
      const shp = shipmentsById[cid];
      if (!shp) continue;
      loadSum += isADRPoints
        ? (shp.loads?.[2] ?? 0)
        : (shp.loads?.[dimensionIndex] ?? 0);
    }
    // --- Visual math ---
    const arrivalBefore = arrivalLoad;
    const afterUnload = Math.max(arrivalBefore - unloadSum, 0);
    // Cap pickup by free space AT ARRIVAL to avoid double-counting delivery space in the stack
    const freeAtArrival = Math.max(capacity - arrivalBefore, 0);
    const pickupDisplay = Math.min(loadSum, freeAtArrival);
    // Remaining also measured against arrivalBefore + pickupDisplay
    const remaining = Math.max(capacity - (arrivalBefore + pickupDisplay), 0);

    result.push({
      stopName,
      utilization: afterUnload,
      delivery: unloadSum,
      pickup: pickupDisplay,
      remainingCapacity: remaining,
    });

    // Update real state for next stop with ACTUAL pickup (uncapped)
    arrivalLoad = afterUnload + loadSum;
  }

  return result;
};